import { useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { TooltipProps } from "@mui/material/Tooltip";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../constants/dialogZIndex";

const OVERFLOW_EPS = 1;

const TOOLTIP_PADDING = 8;
const TOOLTIP_MAX_HEIGHT = 220;
/** Same dark tooltip surface as Start Auto-Tagging / Impacted Concepts. */
const TOOLTIP_BG_COLOR = "rgba(50, 50, 50, 0.95)";

/**
 * Shared dark tooltip chrome for Classification AI Plan tables / toolbars.
 */
export const AI_PLAN_TOOLTIP_SLOT_PROPS: NonNullable<TooltipProps["slotProps"]> =
  {
    popper: {
      sx: {
        zIndex: (theme: { zIndex: { modal: number } }) =>
          theme.zIndex.modal +
          ELEVATED_DIALOG_Z_INDEX_OFFSET +
          ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
      },
    },
    tooltip: {
      sx: {
        maxWidth: 420,
        padding: `${TOOLTIP_PADDING}px`,
        bgcolor: TOOLTIP_BG_COLOR,
        color: "#FFFFFF",
        borderRadius: "4px",
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "20px",
        "& .MuiTooltip-arrow": { color: TOOLTIP_BG_COLOR },
      },
    },
  };

/** Scrolls inside the tooltip padding so the scrollbar stays clear of the border. */
const TOOLTIP_SCROLL_SX = {
  maxHeight: TOOLTIP_MAX_HEIGHT - TOOLTIP_PADDING * 2,
  overflowY: "auto",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  textAlign: "left",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255, 255, 255, 0.35) transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-button": { display: "none" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.35)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.55)",
  },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
} as const;

const CELL_TEXT_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  minWidth: 0,
  maxWidth: "100%",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
} as const;

export function TruncatedTooltipCell({
  text,
  sx,
}: Readonly<{ text: string; sx?: SxProps<Theme> }>) {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      setTruncated(false);
      return;
    }
    const measure = () => {
      setTruncated(el.scrollWidth > el.clientWidth + OVERFLOW_EPS);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  return (
    <Tooltip
      title={<Box sx={TOOLTIP_SCROLL_SX}>{text}</Box>}
      arrow
      placement="top"
      disableHoverListener={!truncated}
      slotProps={AI_PLAN_TOOLTIP_SLOT_PROPS}
    >
      <Box component="span" ref={ref} sx={sx ? [CELL_TEXT_SX, sx] : CELL_TEXT_SX}>
        {text}
      </Box>
    </Tooltip>
  );
}
